import { useState } from "react";
import { useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { Dialog } from "@headlessui/react";
import { useTranslation } from "react-i18next";

import EConsentDialog from "./EConsentDialog";
import DialogBackdrop from "components/ModalDialogs/DialogBackdrop";
import EverlakeLoadingOverlay from "components/General/EverlakeLoadingOverlay";
import { updateUserEmailOptions } from "service/userservice/updateUserEmailOptions";

export default function UserNotificationDialog({ showDialog, setShowDialog }) {
  const { t } = useTranslation();
  const [view, setView] = useState(1);
  const { csrUser } = useSelector((s) => s.csrUser);
  const { user } = useSelector((state) => state.user);
  const { policies } = useSelector((state) => state.data);
  const { register, handleSubmit, reset, getValues, setValue } = useForm();

  const [loading, setLoading] = useState(false);
  const [openEConsentDialog, setOpenEConsentDialog] = useState(false);
  const [selectedPolicy, setSelectedPolicy] = useState("");
  const [previousSelection, setPreviousSelection] = useState("");

  const resetSelection = (val) => {
    if (val === "cancel") {
      setValue(selectedPolicy, previousSelection);
    }
  };

  const getpolicyFlag = (inputflag) => {
    if (inputflag === "" || inputflag === "NN") {
      return "mail";
    } else if (inputflag === "EC" || inputflag === "EU") {
      return "eDelivery";
    } else if (inputflag === "PC" || inputflag === "PU") {
      return "both";
    }
  };
  const handleClick = (policyId, value) => {
    const prevSelection = getValues(policyId);
    setSelectedPolicy(policyId);
    setPreviousSelection(prevSelection);
    if (value !== "mail" && prevSelection !== value) {
      setOpenEConsentDialog(true);
    }
  };

  const updateEmailOption = (flag) => {
    if (flag === "mail") {
      return "NN";
    } else if (flag === "eDelivery") {
      return "EC";
    } else if (flag === "both") {
      return "PC";
    }
  };

  const notEligibleMessage = () => {
    if (policies.find((e) => e.issueState.toUpperCase() === "CA")) {
      return t("CustomerNotificationDialog-notEligibleMessage");
    }
  };

  async function submit(data) {
    const consentRequired = Object.values(data).filter(
      (data) => data && data !== "mail"
    );
    if (!csrUser?.userId) {
      if (view === 1 && consentRequired.length) {
        policies.map(async (policy) => {
          const flag = data[policy.contractID];
          const updatedEmailOption = updateEmailOption(flag);
          const policyOwnerParty = policy.parties?.find(
            (x) => x.person?.type?.toLowerCase() === "primary owner"
          );
          setLoading(true);
          await updateUserEmailOptions(
            user?.userId,
            policy.contractID,
            policy.oldPolicy,
            updatedEmailOption,
            policyOwnerParty.person?.emailOption,
            policyOwnerParty.person?.partyTypeCode +
              policyOwnerParty?.person?.personId
          ).catch((error) => {
            console.log(error);
          });
        });
        setView(2);
        setLoading(false);
      } else if (!consentRequired.length) {
        setView(3);
      } else {
        closeNotification();
      }
    } else {
      alert(t("CustomerNotificationDialog-noPermission"));
      closeNotification();
    }
  }

  const closeNotification = () => {
    setView(1);
    setShowDialog(false);
    reset();
  };

  return (
    <Dialog
      open={showDialog}
      onClose={() => {
        closeNotification();
      }}
      className="ui-dialogs"
    >
      <DialogBackdrop />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel
          role="dialog"
          aria-modal="true"
          className={`ui-default-dialog ${
            view === 1 && policies.length > 5 ? "bottom-[30%]" : ""
          }`}
        >
          <Dialog.Title className="font-body text-xl font-bold text-left">
            {t("CustomerNotificationDialog-title")}
          </Dialog.Title>
          <button
            className="ui-default-dialog-close"
            aria-label="close manage notification preferences window"
            onClick={() => {
              closeNotification();
            }}
          >
            <svg
              className="ui-icon h-3.5 w-3.5"
              focusable="false"
              aria-hidden="true"
              role="presentation"
            >
              <use href="#ui-icon-close"></use>
            </svg>
          </button>
          <hr />
          {view === 1 && (
            <Dialog.Description>
              {t("CustomerNotificationDialog-subtitle")}
            </Dialog.Description>
          )}
          <EverlakeLoadingOverlay isLoading={loading}>
            {view === 1 && (
              <form onSubmit={handleSubmit(submit)}>
                <div className="grid gap-x-2 content-center auto-rows-min">
                  <div>{/* empty cell for grid */}</div>
                  <div className="text-sm font-semibold px-4 text-center justify-center">
                    {t("CustomerNotificationDialog-eDelivery")}
                  </div>
                  <div className="text-sm font-semibold px-4 text-center justify-center">
                    {t("CustomerNotificationDialog-mail")}
                  </div>
                  <div className="text-sm font-semibold px-4 text-center justify-center">
                    {t("CustomerNotificationDialog-both")}
                  </div>
                  {/* E-Delivery Confirmation */}
                  {policies.map((policy, idx) => {
                    const policyOwnerParty = policy.parties?.find(
                      (x) => x.person?.type?.toLowerCase() === "primary owner"
                    );
                    const policyEmailOption =
                      policyOwnerParty?.person?.emailOption;
                    const flag = getpolicyFlag(policyEmailOption);
                    const policyId = policy.contractID;
                    const policyState = policy.issueState;
                    // For CA users enable Email/Both only if 1 of them is true from BE.
                    const shouldEnable =
                      policyState === "CA" &&
                      !["eDelivery", "both"].includes(flag);
                    return (
                      <fieldset
                        className="contents"
                        key={`policy notification - ${idx}`}
                      >
                        <legend className="text-base font-semibold py-3">
                          {t("CustomerNotificationDialog-policy-prefix")}
                          {policy.oldPolicy}
                          {policyState === "CA" ? "*" : ""}
                        </legend>
                        <label className="ui-checkbox relative place-self-center p-1">
                          <input
                            id={`emailOptions-email-${policyId}`}
                            key={`emailOptions-email-${policyId}`}
                            type="radio"
                            disabled={shouldEnable}
                            value={"eDelivery"}
                            defaultChecked={flag === "eDelivery"}
                            onClick={() => handleClick(policyId, "eDelivery")}
                            {...register(policyId)}
                          />
                          <span className="sr-only">
                            {t("CustomerNotificationDialog-eDelivery-sr")}
                          </span>
                          <div
                            className={
                              shouldEnable
                                ? "h-3.5 w-3.5 border rounded bg-ui-gray-light border-blue-500"
                                : "ui-checkmark mx-2"
                            }
                          >
                            {!shouldEnable && (
                              <svg
                                className="ui-icon w-2 h-2 text-white"
                                focusable="false"
                                aria-hidden="true"
                                role="presentation"
                              >
                                <use href="#ui-icon-checkmark"></use>
                              </svg>
                            )}
                          </div>
                        </label>
                        <label className="ui-checkbox relative place-self-center p-1">
                          <input
                            id={`emailOptions-mail-${policyId}`}
                            key={`emailOptions-mail-${policyId}`}
                            type="radio"
                            disabled={false}
                            value={"mail"}
                            defaultChecked={flag === "mail"}
                            onClick={() => handleClick(policyId, "mail")}
                            {...register(policyId)}
                          />
                          <span className="sr-only">
                            {t("CustomerNotificationDialog-mail-sr")}
                          </span>
                          <div className="ui-checkmark mx-2">
                            <svg
                              className="ui-icon w-2 h-2 text-white"
                              focusable="false"
                              aria-hidden="true"
                              role="presentation"
                            >
                              <use href="#ui-icon-checkmark"></use>
                            </svg>
                          </div>
                        </label>
                        <label className="ui-checkbox relative place-self-center p-1">
                          <input
                            id={`emailOptions-both-${policyId}`}
                            key={`emailOptions-both-${policyId}`}
                            type="radio"
                            disabled={shouldEnable}
                            value={"both"}
                            defaultChecked={flag === "both"}
                            onClick={() => handleClick(policyId, "both")}
                            {...register(policyId)}
                          />
                          <span className="sr-only">
                            {t("CustomerNotificationDialog-both-sr")}
                          </span>
                          <div
                            className={
                              shouldEnable
                                ? "h-3.5 w-3.5 border rounded bg-ui-gray-light border-blue-500"
                                : "ui-checkmark mx-2"
                            }
                          >
                            {!shouldEnable && (
                              <svg
                                className="ui-icon w-2 h-2 text-white"
                                focusable="false"
                                aria-hidden="true"
                                role="presentation"
                              >
                                <use href="#ui-icon-checkmark"></use>
                              </svg>
                            )}
                          </div>
                        </label>
                        <hr className="ui-no-margins col-span-4" />
                      </fieldset>
                    );
                  })}
                  <p className="ui-no-margins col-span-4">
                    {notEligibleMessage()}
                  </p>
                </div>

                {/* <!-- Buttons --> */}
                <div className="block text-right mt-6">
                  <button
                    type="button"
                    className="ui-btn-secondary ui-btn-min-w mt-4"
                    onClick={() => {
                      closeNotification();
                    }}
                  >
                    {t("CustomerNotificationDialog-close")}
                    <span className="sr-only">
                      {t("CustomerNotificationDialog-close-sr")}
                    </span>
                  </button>
                  <button className="ui-btn-primary ui-btn-min-w mt-4">
                    {t("CustomerNotificationDialog-save")}
                    <span className="sr-only">
                      {t("CustomerNotificationDialog-save-sr")}
                    </span>
                  </button>
                </div>
              </form>
            )}
          </EverlakeLoadingOverlay>
          {view === 2 && (
            <>
              <div className="p-4 mx-auto md:p-6">
                <div className="text-xl text-center font-semibold">
                  {t("CustomerNotificationDialog-view-2")}
                </div>
                <div className="text-center mt-4 md:mt-6">
                  <button
                    type="button"
                    className="ui-btn-secondary ui-btn-min-w mt-4"
                    onClick={() => closeNotification()}
                  >
                    {t("CustomerNotificationDialog-close")}
                  </button>
                </div>
              </div>
            </>
          )}
          {view === 3 && (
            <>
              <div className="p-4 mx-auto md:p-6">
                <div className="text-xl text-center font-semibold">
                  {t("CustomerNotificationDialog-view-3")}
                </div>
                <div className="text-center mt-4 md:mt-6">
                  <button
                    type="button"
                    className="ui-btn-secondary ui-btn-min-w mt-4"
                    onClick={() => closeNotification()}
                  >
                    {t("CustomerNotificationDialog-close")}
                    <span className="sr-only">
                      {t("CustomerNotificationDialog-close-sr")}
                    </span>
                  </button>
                </div>
              </div>
            </>
          )}
        </Dialog.Panel>
      </div>
      <EConsentDialog
        showEConsentDialog={openEConsentDialog}
        setshowEConsentDialog={setOpenEConsentDialog}
        resetSelection={resetSelection}
      />
    </Dialog>
  );
}
